import type { GasVolumeCompressibilityCalculator } from "../d";

export const dranchukCompressibilityCalculator: GasVolumeCompressibilityCalculator =
  params => {
    const { inSituPressurePSIA, temperatureInRankine, gasSpecificGravity } =
      params;

    // D97: =(4.6+0.1*D32-0.258*D32^2)*10.1325*14.7
    const psuedoCriticalPressurePSIADranchuk =
      (4.6 + 0.1 * gasSpecificGravity - 0.258 * gasSpecificGravity ** 2) *
      10.1325 *
      14.7;

    // D99: =(99.3+180*D32-6.94*D32^2)*1.8
    const psuedoCriticalTemperatureRankineDranchuk =
      (99.3 + 180 * gasSpecificGravity - 6.94 * gasSpecificGravity ** 2) * 1.8;

    // D101: =D3/D97
    const psuedoReducedPressureDranchuk =
      inSituPressurePSIA / psuedoCriticalPressurePSIADranchuk;

    // D103: =D27/D99
    const psuedoReducedTemperatureDranchuk =
      temperatureInRankine / psuedoCriticalTemperatureRankineDranchuk;

    // // New part added F116 from /static/Friction Calcs Updated_08-21-25_updated gas accounting.xlsx
    /**
     * Calculates the Z-factor for a given assumed Z, Tpr, and Ppr.
     */
    const calculateZ = (zAssumed: number, Tpr: number, Ppr: number) => {
      const pr = (0.27 * Ppr) / (zAssumed * Tpr);

      const term1 =
        0.3265 -
        1.07 / Tpr -
        0.5339 / Tpr ** 3 +
        0.01569 / Tpr ** 4 -
        0.05165 / Tpr ** 5;

      const term2 = 0.5475 - 0.7361 / Tpr + 0.1844 / Tpr ** 2;

      const term3 = -0.7361 / Tpr + 0.1844 / Tpr ** 2;

      const expTerm = Math.exp(-0.721 * pr ** 2);

      const z =
        1 +
        term1 * pr +
        term2 * pr ** 2 -
        0.1056 * term3 * pr ** 5 +
        0.6134 * (1 + 0.721 * pr ** 2) * (pr ** 2 / Tpr ** 3) * expTerm;

      return { z, pr };
    };

    /**
     * Solves for the Z-factor using a multi-stage grid search that refines
     * the step size at each stage.
     */
    const solveZFactor = (
      Tpr: number,
      Ppr: number,
      stages: { step: number; maxIterations: number }[]
    ) => {
      const MIN_STARTING_Z = 0.01;
      let searchStart_Z = MIN_STARTING_Z;
      let bestResult = { error: 0, zAssumed: searchStart_Z, z: 1, pr: 0 };

      stages.forEach((stage, stageIndex) => {
        let minError = Infinity;
        let lastError = Infinity;
        let zAssumed = searchStart_Z;
        for (let i = 0; i < stage.maxIterations; i++) {
          const { z, pr } = calculateZ(zAssumed, Tpr, Ppr);
          const currentError = Math.abs(zAssumed - z);
          if (currentError < minError) {
            minError = currentError;
            bestResult = { error: currentError, zAssumed, z, pr };
          }
          // For stages after the first, if error grows, we've passed the
          // minimum for this granularity. Stop this stage and prepare for
          // the next, finer stage.
          if (stageIndex > 0 && i > 0 && currentError > lastError) {
            break;
          }

          lastError = currentError;
          zAssumed += stage.step;
        }

        // Prepare for the next stage by starting the search just before
        // the best-found Z from the current stage.
        searchStart_Z = bestResult.zAssumed - stage.step;
        if (searchStart_Z < MIN_STARTING_Z) searchStart_Z = MIN_STARTING_Z;
      });

      return bestResult;
    };

    // Define the search stages: start coarse, then get progressively finer.
    const searchStages = [
      { step: 0.05, maxIterations: 100 }, // Coarse search
      { step: 0.001, maxIterations: 200 }, // Fine search
      { step: 0.00001, maxIterations: 300 } // Ultra-fine search for precision
    ];

    const { z, pr } = solveZFactor(
      psuedoReducedTemperatureDranchuk,
      psuedoReducedPressureDranchuk,
      searchStages
    );

    const gasCompressibilityFactor = Math.max(z, 0);

    return {
      gasCompressibilityFactor,
      intermediates: {
        pr,
        psuedoCriticalTemperatureRankineDranchuk,
        psuedoCriticalPressurePSIADranchuk,
        psuedoReducedTemperatureDranchuk,
        psuedoReducedPressureDranchuk
      }
    };
  };
